import React from 'react';
import { TableCell, Box } from '@mui/material';
import _ from 'lodash';
import {
  prjDeptHeaderTyps,
  prjOrderHeaderTyps,
  prjSalesHeaderTyps,
  prjStatHeaderTyps,
  prjTargetRateHeaderTyps,
  prjExTargetRateHeaderTyps,
  prjRealSalesHeaderTyps,
} from '../../utils/forecastStaticData';
import { getNumberComma } from '../../utils/numberUtils';

/**
 * @param onCellClick: 셀 클릭 핸들러
 * @param clickOn: 클릭 기능 활성화 여부 스위치
 */
const SalesStaticTableCell = ({ headerRows, data, isTotalRow, selectPrjTyp, useKorean = true, onCellClick, clickOn = false }) => {
  if (!headerRows || !data) return null;

  const formatKoreanUnit = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num === 0) return { upper: '0', lower: '' };
    const absNum = Math.abs(Math.floor(num));
    const sign = num < 0 ? '-' : '';
    const eok = Math.floor(absNum / 100000000); 
    const man = Math.floor((absNum % 100000000) / 10000); 
    const rest = absNum % 10000; 

    let upper = '';
    let lower = '';
    if (eok > 0) {
      upper = `${sign}${getNumberComma(eok)}억`;
      const manPart = man > 0 ? `${getNumberComma(man)}만 ` : '';
      const restPart = rest > 0 ? `${getNumberComma(rest)}` : '';
      lower = (manPart + restPart).trim();
    } else if (man > 0) {
      upper = `${sign}${getNumberComma(man)}만`;
      lower = rest > 0 ? getNumberComma(rest) : '';
    } else {
      upper = `${sign}${getNumberComma(rest)}`;
      lower = '';
    }
    return { upper, lower };
  };

  return (
    headerRows &&
    headerRows.map((field, fIndex) => {
      if (field.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

      const headerTyp =
        field?.id === 'STATRATE' ? prjTargetRateHeaderTyps :
        field?.id === 'EXSTATRATE' ? prjExTargetRateHeaderTyps :
        field?.id?.includes('STAT') ? prjStatHeaderTyps : 
        field?.id === 'OA' ? prjRealSalesHeaderTyps : 
        field?.id === 'HM' ? prjDeptHeaderTyps : 
        prjSalesHeaderTyps;

      return headerTyp.map((type, tIndex) => {
        if (type.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

        const key = `${fIndex}_${tIndex}`;
        let value = _.has(data, `${field.val}.${type.id}`) ? data[field.val][type.id] : '-';
        let fontColor = '#202124'; 
        let fontWeight = isTotalRow ? 600 : 500; 
        let isPercentage = type.id === 'salesProfitMargin' || type.id?.toLowerCase().includes('rate');

        // [핵심 조건] clickOn이 true이고, 매출/매입 항목이며, 소계행이 아닐 때만 활성화  
        const isClickable = clickOn && (type.id === 'sales' || type.id === 'purchase' || type.id === 'collaborationBenefits' || type.id === 'collaborationCost') && !isTotalRow;

        let displayContent;
        if (isPercentage) {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || !isFinite(numValue) || numValue === 0) {
            displayContent = '0 %';
            fontColor = '#70757a'; 
            fontWeight = 400;      
          } else {
            displayContent = `${numValue.toFixed(0)} %`;
            if (numValue >= 70) { fontColor = '#1967d2'; fontWeight = 500; }
            else if (numValue >= 40) { fontColor = '#5e35b1'; fontWeight = 500; }
            else { fontColor = '#d93025'; fontWeight = 500; }
          }
        } else {
          if (value === 0 || value === '0' || value === '-') {
            displayContent = value === '-' ? '-' : '0';
            fontColor = (value === 0 || value === '0') ? '#80868b' : '#202124';
          } else {
            if (useKorean) {
              const { upper, lower } = formatKoreanUnit(value);
              displayContent = (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
                  <span style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: fontWeight, 
                    color: isClickable ? '#1a73e8' : (field.id === 'ES' ? '#311b92' : fontColor),
                    textDecoration: isClickable ? 'underline' : 'none',
                    textUnderlineOffset: '2px'
                  }}>
                    {upper}
                  </span>
                  {lower && (
                    <span style={{ fontSize: '0.75rem', color: '#989999', fontWeight: 500, marginTop: '1px', letterSpacing: '-0.2px' }}>
                      {lower}
                    </span>
                  )}
                </Box>
              );
            } else {
              displayContent = getNumberComma(value);
            }
            if (field.id === 'ES') {
              fontColor = type.id === 'revenue' ? '#1a237e' : '#311b92'; 
              fontWeight = 600;
            }
          }
        }

        return (
          <TableCell
            key={key}
            align={isPercentage ? "center" : "right"} 
            onClick={() => isClickable && onCellClick && onCellClick(data, field, type)}
            sx={{
              textAlign: isPercentage ? 'center' : 'right',
              width: isPercentage ? (useKorean ? '60px !important' : '60px') : (useKorean ? 'auto' : 'max-content'), 
              minWidth: isPercentage ? (useKorean ? '60px !important' : '60px') : (useKorean ? 'max-content' : '110px'),
              padding: isPercentage ? '4px 2px' : '4px 8px',
              whiteSpace: 'nowrap',
              bgcolor: isTotalRow ? (data.id === 'one' ? '#f5f5f5' : '#edf2f7') : 'transparent', 
              color: fontColor,
              fontWeight: fontWeight,
              fontVariantNumeric: 'tabular-nums', 
              borderRight: tIndex === headerTyp.length - 1 
                ? `1px solid ${field.id === 'OA' ? '#d2e3fc' : field.id === 'HM' ? '#f3e5f5' : '#ede7f6'}`
                : '1px solid #f1f3f4',
              cursor: isClickable ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: isClickable ? 'rgba(25, 103, 210, 0.08)' : (isTotalRow ? (data.id === 'one' ? '#f5f5f5' : '#edf2f7') : 'transparent'),
              }
            }}
          >
            {!useKorean || isPercentage || value === 0 || value === '0' || value === '-' ? (
              <span style={{ 
                fontSize: '0.8rem', 
                fontWeight: fontWeight,
                display: isPercentage ? 'inline-block' : 'inline',
                width: isPercentage ? '100%' : 'auto',
                textDecoration: isClickable ? 'underline' : 'none',
                textUnderlineOffset: '2px',
                color: isClickable ? '#1a73e8' : 'inherit'
              }}>
                {displayContent}
              </span>
            ) : (
              displayContent
            )}
          </TableCell>
        );
      });
    })
  );
};

export default SalesStaticTableCell;
